import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { graphql } from '@apollo/react-hoc';
import get from 'lodash/get';
import set from 'lodash/set';
import isNil from 'lodash/isNil';
import { withRouter } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import CommunityNavigation from '../../components/CommunityNavigation';
import HSUpdateForm from '../../components/HSUpdateForm';
import { SimpleLoadingComponent } from '../../utils/apollo';
import cmsHighSchoolDetails from './cms-highschool-details';
import updateHighSchoolMutation from './updateHighSchoolMutation';
import ErrorPage from '../ErrorPage';

import './styles.css';

const fieldMappings = {
  titleStatus: 'academic.field_hs_el_titlei_school_status',
  titleEligible: 'academic.field_hs_el_titleieligible_schoo',
  address: 'contact.field_hs_elna_address_line1',
  state: 'contact.field_hs_elna_state_latest',
  city: 'contact.field_hs_elna_city',
  county: 'contact.field_hs_el_county_name',
  country: 'contact.field_hs_na_country_name_ref',
  zip: 'contact.field_hs_eln_zip',
  fax: 'contact.field_hs_na_fax',
  phone: 'contact.field_hs_elna_phone',
  website: 'contact.field_hs_na_url',
  charterSchool: 'academic.field_hs_el_charter_school',
  coeducational: 'academic.field_hs_el_coeducational',
  collegeGoingRate: 'academic.field_hs_na_percent_college',
  studentTeacherRatio: 'academic.field_hs_el_pupil_teacher_ratio',
  fullTimeTeachers: 'academic.field_hs_el_fte_teachers',
  highestGrade: 'academic.field_hs_elna_highest_grade',
  lowestGrade: 'academic.field_hs_elna_lowest_grade',
  schoolLevel: 'academic.field_hs_el_school_level',
  schoolPublicType: 'academic.field_hs_el_school_type_public',
  schoolPrivateType: 'academic.field_hs_el_school_type_private',
  lastReportedYear: 'academic.field_hs_el_years_sch_rep_data',
  male: 'enrollmentByGender.field_hs_el_male_students',
  female: 'enrollmentByGender.field_hs_el_female_students',
  nonBinary: 'enrollmentByGender.field_hs_el_nonbinary_students',
  asianPacificIsl: 'enrollmentByEthnicity.field_hs_el_asianpacificisl_stud',
  amIndAlasNat: 'enrollmentByEthnicity.field_hs_el_am_ind_alas_nat_stud',
  hawaiNatPcfIsl: 'enrollmentByEthnicity.field_hs_el_hawai_natpcfisl_stud',
  twoOrMoreRaces: 'enrollmentByEthnicity.field_hs_el_twoormore_races_stud',
  white: 'enrollmentByEthnicity.field_hs_el_white_students',
  black: 'enrollmentByEthnicity.field_hs_el_black_students',
  hispanic: 'enrollmentByEthnicity.field_hs_el_hispanic_students',
  preKindergarten: 'enrollmentByGrade.field_hs_el_prekinder_students',
  kindergarten: 'enrollmentByGrade.field_hs_el_kindergart_students',
  grade1: 'enrollmentByGrade.field_hs_el_grade_1_students',
  grade2: 'enrollmentByGrade.field_hs_el_grade_2_students',
  grade3: 'enrollmentByGrade.field_hs_el_grade_3_students',
  grade4: 'enrollmentByGrade.field_hs_el_grade_4_students',
  grade5: 'enrollmentByGrade.field_hs_el_grade_5_students',
  grade6: 'enrollmentByGrade.field_hs_el_grade_6_students',
  grade7: 'enrollmentByGrade.field_hs_el_grade_7_students',
  grade8: 'enrollmentByGrade.field_hs_el_grade_8_students',
  grade9: 'enrollmentByGrade.field_hs_el_grade_9_students',
  grade10: 'enrollmentByGrade.field_hs_el_grade_10_students',
  grade11: 'enrollmentByGrade.field_hs_el_grade_11_students',
  grade12: 'enrollmentByGrade.field_hs_el_grade_12_students',
};

// any numeric attributes changed in the UI will be string type
// and need to be transformed back to number
const _parseInt = (value) => (value ? parseInt(value, 10) : value);
const fieldTransforms = {
  amIndAlasNat: _parseInt,
  asianPacificIsl: _parseInt,
  black: _parseInt,
  collegeGoingRate: _parseInt,
  female: _parseInt,
  nonBinary: _parseInt,
  kindergarten: _parseInt,
  preKindergarten: _parseInt,
  grade1: _parseInt,
  grade10: _parseInt,
  grade11: _parseInt,
  grade12: _parseInt,
  grade2: _parseInt,
  grade3: _parseInt,
  grade4: _parseInt,
  grade5: _parseInt,
  grade6: _parseInt,
  grade7: _parseInt,
  grade8: _parseInt,
  grade9: _parseInt,
  hawaiNatPcfIsl: _parseInt,
  hispanic: _parseInt,
  male: _parseInt,
  twoOrMoreRaces: _parseInt,
  white: _parseInt,
};

export class EditInstitutionComponent extends React.PureComponent {
  static propTypes = {
    viewer: PropTypes.shape({
      id: PropTypes.string.isRequired,
      hsInstitutionId: PropTypes.string.isRequired,
      cmsHighSchool: PropTypes.object,
    }).isRequired,
    mutateUpdateHighSchool: PropTypes.func.isRequired,
    history: PropTypes.shape({
      replace: PropTypes.func.isRequired,
    }).isRequired,
  };

  state = {
    updateError: false,
    requesting: false,
  };

  buildHighSchoolInput() {
    let highSchoolInput = {};
    if (this.props.viewer && this.props.viewer.cmsHighSchool) {
      const { cmsHighSchool } = this.props.viewer;
      highSchoolInput.name = cmsHighSchool.name;
      highSchoolInput.isPublic = cmsHighSchool.field_hs_elna_school_type === 'PUBLIC';
      highSchoolInput = Object.keys(fieldMappings).reduce((obj, key) => {
        obj[key] = get(cmsHighSchool, fieldMappings[key]);
        return obj;
      }, highSchoolInput);
    }
    return highSchoolInput;
  }

  buildHighSchoolMutation(updatedHighSchool) {
    const isFieldChanged = (originalValue, newValue) => (
      (isNil(originalValue) && !isNil(newValue)) ||
      (!isNil(originalValue) && isNil(newValue)) ||
      (!isNil(originalValue) && !isNil(newValue) && originalValue !== newValue)
    );
    const { cmsHighSchool } = this.props.viewer;
    const highSchoolMutationInput = Object.keys(fieldMappings).reduce((obj, key) => {
      const originalFieldValue = get(cmsHighSchool, fieldMappings[key]);
      // any numeric attributes changed in the UI will now be string type, transform back to number
      const newValue = fieldTransforms[key] ?
        fieldTransforms[key](updatedHighSchool[key]):
        updatedHighSchool[key];
      if (isFieldChanged(originalFieldValue, newValue)) {
        set(obj, fieldMappings[key], newValue);
      }
      return obj;
    }, {});
    return highSchoolMutationInput;
  }

  navigateToCaller() {
    const path = `/community/institution-hs-id/${this.props.viewer.hsInstitutionId}`;
    this.props.history.replace(path);
  }

  _handleCancel = () => {
    this.setState({ updateError: false, requesting: false });
    this.navigateToCaller();
  };

  _handleSave = (updatedHighSchool) => {
    this.setState({ updateError: false, requesting: true });

    const onFailure = (err) => {
      this.setState({ updateError: true, requesting: false });
    };

    const onSuccess = (data) => {
      this.setState({ updateError: false, requesting: false });
      this.navigateToCaller();
    };
    const highSchoolMutationInput = this.buildHighSchoolMutation(updatedHighSchool);
    this.props.mutateUpdateHighSchool({
      variables: {
        input: {
          highSchool: highSchoolMutationInput,
        },
      },
    }).then(onSuccess, onFailure);
  };

  render() {
    const { updateError, requesting } = this.state;
    const highSchool = this.buildHighSchoolInput();
    if (Object.keys(highSchool).length === 0) {
      return (<ErrorPage />);
    } else {
      return (
        <div styleName="wrapper">
          <PageTitle title="High School Profile" category="Naviance" />
          <div styleName="frame">
            <div styleName="page">
              <CommunityNavigation activeTab="institution" />
              <div styleName="main-wrapper">
                <HSUpdateForm
                  onSave={this._handleSave}
                  onCancel={this._handleCancel}
                  updateError={updateError}
                  requesting={requesting}
                  highSchool={highSchool}
                />
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
}

const query = gql`
  query EditInstitution {
    viewer {
      id
      hsInstitutionId
      cmsHighSchool {
        id
        ...CMSHighSchoolDetails
      }
    }
  }
  ${cmsHighSchoolDetails}
`;

const graphqlData = graphql(query);

const updateHighSchool = graphql(updateHighSchoolMutation, {
  name: 'mutateUpdateHighSchool',
});

export default SimpleLoadingComponent(graphqlData, updateHighSchool)(withRouter(EditInstitutionComponent));
